<template>
  <div class="app">
    <vue-progress-bar></vue-progress-bar>

    <component :is="layout">
      <router-view></router-view>
    </component>
  </div>
</template>

<script>
import Vue from "vue";
import "@/styles/app.scss";
import { createNamespacedHelpers } from 'vuex';
  import { tryStartSignalRConnection, tryStopSignalRConnection } from '@/services/signalr-service';
const { mapGetters, mapMutations } = createNamespacedHelpers('user');

const defaultLayout = "default";

export default {
  data() {
    return {
      updateLayoutCounter: 0
    };
  },

  computed: {
    ...mapGetters(['showProfile']),
  
    layout() {
      const layoutName = this.$route.meta.layout || defaultLayout;

      return `${layoutName}-layout`;
    }
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        document.title = to.meta.title ? `Precision Health Reports | ${to.meta.title}` : 'Precision Health Reports';
      }
    },
    showProfile(to) {
      document.body.classList.toggle('side-modal-open', to);
    }
  },

  mounted() {
    this.$Progress.finish();

    tryStartSignalRConnection((reportId) => {
      this.$root.errorToast("The order has been released due to your inactivity.");

      const path = this.$route.path;

      if (path === "/reports" || path === `/reports/${reportId}/view`) {
        this.$router.push("/");
      }
    });
  },

  created() {
    this.$Progress.start();
    this.initRouter();
    this.initAjax();

    this.$auth.init(this.forceRedirectTo);
  },

  destroyed() {
    tryStopSignalRConnection();
  },

  methods: {
    ...mapMutations(["setShowProfile"]),
    initRouter() {
      this.$router.beforeEach((to, from, next) => {
        if (to.meta.progress) this.$Progress.parseMeta(to.meta.progress);
        if (this.showProfile) this.setShowProfile(false);
        
        this.$Progress.start();

        next();
      });

      this.$router.afterEach(() => {
        this.$Progress.finish();
      });
    },

    initAjax() {
      this.axios.interceptors.request.use(
        config => {
          if (this.canStartProgress() && this.canShowGlobalLoader(config))
            this.$Progress.start();

          return config;
        },
        error => {
          if (error.request) {
            if (this.canShowGlobalLoader(error.request.config))
              this.$Progress.fail();
          } else {
            Vue.toasted.error("Oops! " + error.message);
            this.$Progress.fail();
          }

          return Promise.reject(error);
        }
      );

      this.axios.interceptors.response.use(
        response => {
          if (this.canShowGlobalLoader(response.config))
            this.$Progress.finish();

          return response;
        },
        error => {
          if (error.response) {
            if (this.canShowGlobalLoader(error.response.config))
              this.$Progress.fail();

            if (error.response.data.error_description) {
              Vue.toasted.error(error.response.data.error_description);
            } else {
              if (error.response.status === 404)
                this.$router.replace({ name: "not-found" });
              else if (!error.response.data.error) {
                Vue.toasted.error("Oops! " + error.message);
              }
              else {
                Vue.toasted.error("Oops! " + error.response.data.error);
              }
            }
          } else {
            Vue.toasted.error("Oops! " + error.message);
            this.$Progress.fail();
          }

          return Promise.reject(error);
        }
      );
    },

    canStartProgress() {
      //check if already started
      return !this.$Progress.$vm.RADON_LOADING_BAR.options.show;
    },

    canShowGlobalLoader(config) {
      if (!config) return true;

      return config.globalLoader !== false;
    },

    successToast(message) {
      Vue.toasted.success(message, { icon: "check" });
    },

    errorToast(message) {
      Vue.toasted.error(message, { icon: "exclamation" });
    },

    forceRedirectTo(toRoute) {
      //skipping anonymous users
      if (!this.$auth.isAuthenticated) return false;

      //navigation to routes that doesn't require authentication is always allowed
      if (toRoute.meta && !toRoute.meta.auth) return false;

      //example
      //if (this.$auth.isInRole(UserRole.Parent) && !this.isParentHasChildren)
      //    return { name: 'parent-my-students' };

      return false;
    }
  }
};
</script>